/**
 * Provenance Score System
 * Documentation completeness scoring system for car collector vehicles
 * Uses car show lingo for tier names
 */

/**
 * Tier definitions for the scoring system
 * Each tier represents a level of documentation completeness
 */
export const SCORE_TIERS = [
  {
    name: 'Barn Find',
    minScore: 0,
    maxScore: 20,
    color: 'rdc-gray',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-700',
    borderClass: 'border-gray-300',
    icon: 'barn',
    description: 'Just discovered. Time to start documenting.',
    classes: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      lightBg: 'bg-gray-50',
      border: 'border-gray-300',
    },
  },
  {
    name: 'Daily Driver',
    minScore: 21,
    maxScore: 50,
    color: 'rdc-blue',
    bgClass: 'bg-blue-100',
    textClass: 'text-blue-700',
    borderClass: 'border-blue-300',
    icon: 'road',
    description: 'Getting there. Add photos and records to level up.',
    classes: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      lightBg: 'bg-blue-50',
      border: 'border-blue-300',
    },
  },
  {
    name: 'Show Ready',
    minScore: 51,
    maxScore: 75,
    color: 'rdc-forest',
    bgClass: 'bg-green-100',
    textClass: 'text-green-700',
    borderClass: 'border-green-300',
    icon: 'trophy',
    description: 'Looking good. A few more details for Concours.',
    classes: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      lightBg: 'bg-green-50',
      border: 'border-green-300',
    },
  },
  {
    name: 'Concours',
    minScore: 76,
    maxScore: 95,
    color: 'rdc-gold',
    bgClass: 'bg-amber-100',
    textClass: 'text-amber-700',
    borderClass: 'border-amber-300',
    icon: 'medal',
    description: 'Exceptional documentation. Almost Best in Show.',
    classes: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      lightBg: 'bg-amber-50',
      border: 'border-amber-300',
    },
  },
  {
    name: 'Best in Show',
    minScore: 96,
    maxScore: 100,
    color: 'rdc-orange',
    bgClass: 'bg-orange-100',
    textClass: 'text-orange-700',
    borderClass: 'border-orange-300',
    icon: 'crown',
    description: 'Complete provenance. The gold standard.',
    classes: {
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      lightBg: 'bg-orange-50',
      border: 'border-orange-300',
    },
  },
];

/**
 * Scoring weights and point values
 * Total possible points: 110 (normalized to 100)
 */
const SCORING_WEIGHTS = {
  photos: {
    base: 20,
    bonuses: [
      { threshold: 3, points: 5 },
      { threshold: 5, points: 5 },
    ],
  },
  vin: 10,
  serviceRecords: {
    base: 20,
    bonuses: [
      { threshold: 3, points: 5 },
    ],
  },
  documents: 15,
  valuation: 15,
  completeDetails: {
    mileage: 3,
    color: 3,
    trim: 3,
    yearMakeModel: 1,
  },
  maintenanceSchedule: 5,
};

/**
 * Calculate the score for individual data points within a category
 * @param {number} count - The count of items (photos, records, etc.)
 * @param {number} basePoints - Base points for having at least 1
 * @param {Array} bonuses - Array of {threshold, points} objects
 * @returns {number} Total points earned
 */
function calculateCategoryScore(count, basePoints, bonuses = []) {
  if (count === 0) return 0;

  let points = basePoints;

  if (bonuses && bonuses.length > 0) {
    for (const bonus of bonuses) {
      if (count >= bonus.threshold) {
        points += bonus.points;
      }
    }
  }

  return points;
}

/**
 * Calculate the score for complete vehicle details
 * Gives partial credit for individual fields
 * @param {object} vehicle - Vehicle object with detail fields
 * @returns {number} Points earned for complete details
 */
function calculateDetailsScore(vehicle) {
  let points = 0;

  // Year, Make, Model should always exist (1 point)
  if (vehicle?.year && vehicle?.make && vehicle?.model) {
    points += SCORING_WEIGHTS.completeDetails.yearMakeModel;
  }

  // Mileage (3 points)
  if (vehicle?.mileage !== null && vehicle?.mileage !== undefined && vehicle.mileage !== '') {
    points += SCORING_WEIGHTS.completeDetails.mileage;
  }

  // Color (3 points)
  if (vehicle?.color !== null && vehicle?.color !== undefined && vehicle.color !== '') {
    points += SCORING_WEIGHTS.completeDetails.color;
  }

  // Trim (3 points)
  if (vehicle?.trim !== null && vehicle?.trim !== undefined && vehicle.trim !== '') {
    points += SCORING_WEIGHTS.completeDetails.trim;
  }

  return points;
}

/**
 * Get the tier information for a given score
 * @param {number} score - Normalized score (0-100)
 * @returns {object} Tier information object
 */
export function getScoreTier(score) {
  const normalizedScore = Math.min(100, Math.max(0, score));
  const tier = SCORE_TIERS.find(
    (t) => normalizedScore >= t.minScore && normalizedScore <= t.maxScore
  );
  return tier || SCORE_TIERS[0]; // Default to "Barn Find"
}

/**
 * Calculate the garage score for a single vehicle
 * @param {object} vehicle - Vehicle object
 * @param {number} serviceRecordCount - Number of service records
 * @param {number} documentCount - Number of documents
 * @param {number} valuationCount - Number of valuations
 * @param {number} photoCount - Number of photos
 * @returns {object} Score information including tier and breakdown
 */
export function calculateVehicleScore(
  vehicle,
  serviceRecordCount = 0,
  documentCount = 0,
  valuationCount = 0,
  photoCount = 0
) {
  const breakdown = {};

  // Photos: base 20, +5 for 3+, +5 for 5+
  breakdown.photos = calculateCategoryScore(
    photoCount,
    SCORING_WEIGHTS.photos.base,
    SCORING_WEIGHTS.photos.bonuses
  );

  // VIN: 10 points
  breakdown.vin = vehicle?.vin ? SCORING_WEIGHTS.vin : 0;

  // Service Records: base 20, +5 for 3+
  breakdown.serviceRecords = calculateCategoryScore(
    serviceRecordCount,
    SCORING_WEIGHTS.serviceRecords.base,
    SCORING_WEIGHTS.serviceRecords.bonuses
  );

  // Documents: 15 points
  breakdown.documents = documentCount > 0 ? SCORING_WEIGHTS.documents : 0;

  // Valuation: 15 points
  breakdown.valuation = valuationCount > 0 ? SCORING_WEIGHTS.valuation : 0;

  // Complete Details: up to 10 points (partial credit)
  breakdown.completeDetails = calculateDetailsScore(vehicle);

  // Maintenance Schedule: 5 points (placeholder - always give 5 if service records exist)
  breakdown.maintenanceSchedule =
    serviceRecordCount > 0 ? SCORING_WEIGHTS.maintenanceSchedule : 0;

  // Calculate total raw score
  const totalRawScore = Object.values(breakdown).reduce((sum, points) => sum + points, 0);

  // Maximum possible points: 110
  // Normalize to 0-100 scale
  const maxPossiblePoints = 110;
  const normalizedScore = Math.round((totalRawScore / maxPossiblePoints) * 100);
  const score = Math.min(100, normalizedScore);

  const tier = getScoreTier(score);

  return {
    score,
    tier: tier.name,
    tierInfo: tier,
    breakdown,
    rawScore: totalRawScore,
    maxPossibleScore: maxPossiblePoints,
  };
}

/**
 * Calculate the average score for a collection of vehicles
 * @param {array} vehicleScores - Array of score objects from calculateVehicleScore
 * @returns {object} Collection-level statistics
 */
export function calculateCollectionScore(vehicleScores) {
  if (!vehicleScores || vehicleScores.length === 0) {
    return {
      averageScore: 0,
      tier: SCORE_TIERS[0].name,
      tierInfo: SCORE_TIERS[0],
      vehicleCount: 0,
      bestInShowCount: 0,
      totalScore: 0,
      breakdown: {
        byTier: {},
      },
    };
  }

  const scores = vehicleScores.map((vs) => vs.score);
  const totalScore = scores.reduce((sum, score) => sum + score, 0);
  const averageScore = Math.round(totalScore / scores.length);

  const bestInShowCount = vehicleScores.filter(
    (vs) => vs.tier === 'Best in Show'
  ).length;

  // Count vehicles by tier
  const byTier = {};
  SCORE_TIERS.forEach((tier) => {
    byTier[tier.name] = vehicleScores.filter((vs) => vs.tier === tier.name).length;
  });

  const collectionTier = getScoreTier(averageScore);

  return {
    averageScore,
    tier: collectionTier.name,
    tierInfo: collectionTier,
    vehicleCount: vehicleScores.length,
    bestInShowCount,
    totalScore,
    breakdown: {
      byTier,
    },
  };
}

/**
 * Get actionable suggestions to improve a vehicle's score
 * @param {object} vehicle - Vehicle object
 * @param {number} serviceRecordCount - Number of service records
 * @param {number} documentCount - Number of documents
 * @param {number} valuationCount - Number of valuations
 * @param {number} photoCount - Number of photos
 * @param {string} vehicleId - ID for constructing action links
 * @returns {array} Array of suggestions to improve score
 */
export function getNextTierActions(
  vehicle,
  serviceRecordCount = 0,
  documentCount = 0,
  valuationCount = 0,
  photoCount = 0,
  vehicleId = null
) {
  const actions = [];
  const editLink = vehicleId ? `/garage/${vehicleId}/edit` : '#';

  // Check for missing/low photos
  if (photoCount === 0) {
    actions.push({
      action: 'Add at least 1 photo to unlock Photo documentation',
      points: 20,
      link: editLink,
      priority: 'high',
    });
  } else if (photoCount < 3) {
    actions.push({
      action: `Add ${3 - photoCount} more photo(s) for a +5 bonus`,
      points: 5,
      link: editLink,
      priority: 'medium',
    });
  } else if (photoCount < 5) {
    actions.push({
      action: `Add ${5 - photoCount} more photo(s) for a +5 bonus`,
      points: 5,
      link: editLink,
      priority: 'low',
    });
  }

  // Check for missing VIN
  if (!vehicle?.vin) {
    actions.push({
      action: 'Add VIN number',
      points: 10,
      link: editLink,
      priority: 'high',
    });
  }

  // Check for missing/low service records
  if (serviceRecordCount === 0) {
    actions.push({
      action: 'Add at least 1 service record to unlock Maintenance documentation',
      points: 25, // Base 20 + maintenance schedule 5
      link: editLink,
      priority: 'high',
    });
  } else if (serviceRecordCount < 3) {
    actions.push({
      action: `Add ${3 - serviceRecordCount} more service record(s) for a +5 bonus`,
      points: 5,
      link: editLink,
      priority: 'medium',
    });
  }

  // Check for missing documents
  if (documentCount === 0) {
    actions.push({
      action: 'Add at least 1 document (title, registration, etc.)',
      points: 15,
      link: editLink,
      priority: 'high',
    });
  }

  // Check for missing valuation
  if (valuationCount === 0) {
    actions.push({
      action: 'Add at least 1 valuation entry',
      points: 15,
      link: editLink,
      priority: 'medium',
    });
  }

  // Check for incomplete details
  const missingDetails = [];
  if (!vehicle?.mileage && vehicle?.mileage !== 0) {
    missingDetails.push('mileage');
  }
  if (!vehicle?.color) {
    missingDetails.push('color');
  }
  if (!vehicle?.trim) {
    missingDetails.push('trim');
  }

  if (missingDetails.length > 0) {
    const detailsPoints = missingDetails.length * 3;
    actions.push({
      action: `Add missing details: ${missingDetails.join(', ')}`,
      points: detailsPoints,
      link: editLink,
      priority: missingDetails.length > 1 ? 'high' : 'medium',
    });
  }

  // Sort by priority (high -> medium -> low) and then by points descending
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  actions.sort((a, b) => {
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    return b.points - a.points;
  });

  return actions;
}

/**
 * Get tier thresholds and progress information
 * @param {number} currentScore - Current score (0-100)
 * @returns {object} Information about current tier and next tier
 */
export function getTierProgress(currentScore) {
  const currentTier = getScoreTier(currentScore);
  const currentTierIndex = SCORE_TIERS.findIndex((t) => t.name === currentTier.name);

  const nextTierIndex = currentTierIndex < SCORE_TIERS.length - 1 ? currentTierIndex + 1 : -1;
  const nextTier = nextTierIndex >= 0 ? SCORE_TIERS[nextTierIndex] : null;

  const pointsUntilNextTier = nextTier ? nextTier.minScore - currentScore : 0;
  const progressInTier = currentScore - currentTier.minScore;
  const tierWidth = currentTier.maxScore - currentTier.minScore;
  const percentInTier = Math.round((progressInTier / tierWidth) * 100);

  return {
    currentTier,
    nextTier,
    pointsUntilNextTier: Math.max(0, pointsUntilNextTier),
    progressInTier,
    tierWidth,
    percentInTier: Math.min(100, Math.max(0, percentInTier)),
  };
}

/**
 * Get a summary of scoring categories and their current status
 * @param {object} scoreBreakdown - Breakdown object from calculateVehicleScore
 * @returns {array} Array of category summaries
 */
export function getScoreBreakdownSummary(scoreBreakdown) {
  const categories = [
    {
      name: 'Photos',
      key: 'photos',
      maxPoints: 30, // 20 base + 10 bonuses
      earned: scoreBreakdown.photos,
    },
    {
      name: 'VIN',
      key: 'vin',
      maxPoints: 10,
      earned: scoreBreakdown.vin,
    },
    {
      name: 'Service Records',
      key: 'serviceRecords',
      maxPoints: 25, // 20 base + 5 bonus
      earned: scoreBreakdown.serviceRecords,
    },
    {
      name: 'Documents',
      key: 'documents',
      maxPoints: 15,
      earned: scoreBreakdown.documents,
    },
    {
      name: 'Valuation',
      key: 'valuation',
      maxPoints: 15,
      earned: scoreBreakdown.valuation,
    },
    {
      name: 'Details',
      key: 'completeDetails',
      maxPoints: 10,
      earned: scoreBreakdown.completeDetails,
    },
    {
      name: 'Maintenance Schedule',
      key: 'maintenanceSchedule',
      maxPoints: 5,
      earned: scoreBreakdown.maintenanceSchedule,
    },
  ];

  return categories.map((category) => ({
    ...category,
    percent: Math.round((category.earned / category.maxPoints) * 100),
    complete: category.earned === category.maxPoints,
  }));
}
